/**
 * Guide engine v3 - package validator (#1023, epic #1020).
 * Spec: docs/plans/guides/spec-v3.md (R1 shape + R14 static review checks 1-9).
 *
 * validateGuidePackage(pkg) throws a clear Error naming the failed check on the
 * first violation, or returns the typed package (R1) when it passes. The loader
 * maps a throw to the error chip (R11). Two passes:
 *   1. Shape: required fields, types, advance/highlight/placement shapes, the
 *      click -> highlight id binding (R1, R4).
 *   2. Graph (R14): a directed graph of steps wired by advance `next`, entered at
 *      `firstStepId`. Checks 1-9; check 10 (no forced infinite loop) falls out of
 *      check 8 (graceful-end reachable from every step).
 *
 * Hand-rolled (no ajv): the contract is small and the error messages must name
 * the exact R14 check for the authoring workflow.
 */

import { EXIT, ADVANCE_KINDS, MAX_MANUAL_BUTTONS } from './contract';
import { isRedHue, isPureWhite } from './palette';

const isNonEmptyString = (v) => typeof v === 'string' && v.length > 0;
const isNumberInRange = (v, lo, hi) => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;

/** A `[data-tour-id="..."]` selector, nothing else (R1.3 / R4 selector rule). */
const DATA_TOUR_ID_SELECTOR = /^\[data-tour-id="[^"]+"\]$/;

/** Default manual-button label token, used for distinctness when `text` is omitted (R14.3). */
const DEFAULT_MANUAL_LABEL = ' default-next';

class GuideValidationError extends Error {
  constructor(message) {
    super(message);
    this.name = 'GuideValidationError';
  }
}

const fail = (msg) => {
  throw new GuideValidationError(msg);
};

/**
 * Shape pass: structural + per-field validation of one step.
 * @param {*} step
 * @param {Set<string>} seenIds
 */
const validateStepShape = (step, seenIds) => {
  if (!step || typeof step !== 'object') fail('SHAPE: steps[] contains a non-object entry');
  if (!isNonEmptyString(step.id)) fail('SHAPE: step missing id');
  if (seenIds.has(step.id)) fail(`SHAPE: duplicate step id: ${step.id}`);
  seenIds.add(step.id);

  if (!isNonEmptyString(step.body)) fail(`SHAPE: step ${step.id} missing body`);
  if (step.heading != null && typeof step.heading !== 'string') fail(`SHAPE: step ${step.id} heading must be a string`);
  if (step.actionHint != null && typeof step.actionHint !== 'string') fail(`SHAPE: step ${step.id} actionHint must be a string`);
  if (step.topicTags != null && (!Array.isArray(step.topicTags) || !step.topicTags.every(isNonEmptyString))) {
    fail(`SHAPE: step ${step.id} topicTags must be an array of strings`);
  }

  // gate / allowExtras (#1207) - opt-in deny-by-default gating.
  if (step.gate != null && typeof step.gate !== 'boolean') fail(`SHAPE: step ${step.id} gate must be a boolean`);
  if (step.allowExtras != null) {
    if (!Array.isArray(step.allowExtras)) fail(`SHAPE: step ${step.id} allowExtras must be an array`);
    for (const sel of step.allowExtras) {
      if (!DATA_TOUR_ID_SELECTOR.test(sel)) {
        fail(`SHAPE: step ${step.id} allowExtras "${sel}" is not a [data-tour-id="..."] selector`);
      }
    }
  }

  // placement (R1.6) - required on every step.
  const p = step.placement;
  if (!p || typeof p !== 'object') fail(`SHAPE: step ${step.id} missing placement`);
  if (!isNumberInRange(p.x, 0, 100)) fail(`SHAPE: step ${step.id} placement.x must be 0-100`);
  if (!isNumberInRange(p.y, 0, 100)) fail(`SHAPE: step ${step.id} placement.y must be 0-100`);
  if (!isNumberInRange(p.width, 0, 100)) fail(`SHAPE: step ${step.id} placement.width must be 0-100`);

  // highlight (R1.3) - optional array.
  const highlightIds = new Set();
  if (step.highlight != null) {
    if (!Array.isArray(step.highlight)) fail(`SHAPE: step ${step.id} highlight must be an array`);
    for (const h of step.highlight) {
      if (!h || typeof h !== 'object') fail(`SHAPE: step ${step.id} highlight entry is not an object`);
      if (!isNonEmptyString(h.element)) fail(`SHAPE: step ${step.id} highlight missing element`);
      if (!isNonEmptyString(h.color)) fail(`SHAPE: step ${step.id} highlight missing color`);
      if (!isNonEmptyString(h.id)) fail(`SHAPE: step ${step.id} highlight missing id`);
      if (highlightIds.has(h.id)) fail(`SHAPE: step ${step.id} duplicate highlight id: ${h.id}`);
      highlightIds.add(h.id);
    }
  }

  // advance (R1.4 / R4) - required, non-empty.
  if (!Array.isArray(step.advance) || step.advance.length === 0) {
    fail(`SHAPE: step ${step.id} advance must be a non-empty array`);
  }
  for (const a of step.advance) {
    if (!a || typeof a !== 'object') fail(`SHAPE: step ${step.id} advance entry is not an object`);
    if (!ADVANCE_KINDS.includes(a.on)) fail(`SHAPE: step ${step.id} advance.on must be one of ${ADVANCE_KINDS.join('|')}`);
    if (!isNonEmptyString(a.next)) fail(`SHAPE: step ${step.id} advance missing next`);
    if (a.on === 'click') {
      if (!isNonEmptyString(a.id)) fail(`SHAPE: step ${step.id} click advance missing id`);
      if (!highlightIds.has(a.id)) fail(`SHAPE: step ${step.id} click advance id "${a.id}" does not match a highlight id`);
    } else if (a.on === 'appears' || a.on === 'value') {
      if (!isNonEmptyString(a.selector)) fail(`SHAPE: step ${step.id} ${a.on} advance missing selector`);
      if (a.on === 'value' && a.equals != null && typeof a.equals !== 'string') {
        fail(`SHAPE: step ${step.id} value advance equals must be a string`);
      }
    } else if (a.on === 'manual') {
      if (a.text != null && typeof a.text !== 'string') fail(`SHAPE: step ${step.id} manual advance text must be a string`);
    }
  }
};

/**
 * R14.3 DISTINCT: a step's advance conditions are mutually distinct - action
 * kinds by trigger (`on` + id/selector), manual conditions by label (R4).
 * @param {*} step
 */
const checkDistinct = (step) => {
  const triggers = new Set();
  const labels = new Set();
  for (const a of step.advance) {
    if (a.on === 'manual') {
      const label = a.text == null ? DEFAULT_MANUAL_LABEL : a.text;
      if (labels.has(label)) {
        fail(`R14.3 DISTINCT: step ${step.id} has two manual buttons with the same label`);
      }
      labels.add(label);
    } else {
      const key = `${a.on} ${a.id || a.selector || ''}`;
      if (triggers.has(key)) {
        fail(`R14.3 DISTINCT: step ${step.id} has two ${a.on} conditions with the same trigger`);
      }
      triggers.add(key);
    }
  }
};

/**
 * Validate a v3 guide package: shape (R1) then the R14 static checks 1-9.
 * @param {unknown} pkg
 * @returns {import('./contract').GuidePackage}
 * @throws {GuideValidationError} naming the failed check.
 */
export const validateGuidePackage = (pkg) => {
  // --- Root shape (R1.1) ---
  if (!pkg || typeof pkg !== 'object') fail('SHAPE: guide package is not an object');
  if (!isNonEmptyString(pkg.moduleId)) fail('SHAPE: guide package missing moduleId');
  if (!isNonEmptyString(pkg.version)) fail('SHAPE: guide package missing version');
  if (!isNonEmptyString(pkg.firstStepId)) fail('SHAPE: guide package missing firstStepId');
  if (!Array.isArray(pkg.steps) || pkg.steps.length === 0) fail('SHAPE: guide package steps[] must be a non-empty array');

  // --- Per-step shape ---
  const seenIds = new Set();
  for (const step of pkg.steps) validateStepShape(step, seenIds);

  const stepIds = seenIds; // every id, now collected
  if (!stepIds.has(pkg.firstStepId)) fail(`SHAPE: firstStepId "${pkg.firstStepId}" is not a step id`);

  // --- R14 checks ---
  for (const step of pkg.steps) {
    for (const a of step.advance) {
      // 1. RESOLVABLE
      if (a.next !== EXIT && !stepIds.has(a.next)) {
        fail(`R14.1 RESOLVABLE: step ${step.id} advance next "${a.next}" is not a step id or ${EXIT}`);
      }
      // 4. NO SELF-NEXT
      if (a.next === step.id) {
        fail(`R14.4 NO_SELF_NEXT: step ${step.id} advance targets itself`);
      }
      // 5. NO RESTART-TO-TOP
      if (a.next === pkg.firstStepId) {
        fail(`R14.5 NO_RESTART_TO_TOP: step ${step.id} advance restarts to firstStepId "${pkg.firstStepId}"`);
      }
      // 9. SELECTORS
      if (a.on === 'appears' || a.on === 'value') {
        if (!DATA_TOUR_ID_SELECTOR.test(a.selector)) {
          fail(`R14.9 SELECTORS: step ${step.id} advance selector "${a.selector}" is not a [data-tour-id="..."] selector`);
        }
      }
    }

    // 3. DISTINCT
    checkDistinct(step);

    // 6. UNIQUE RING COLOR + non-red + off-white
    const colors = new Set();
    for (const h of step.highlight || []) {
      const lc = h.color.toLowerCase();
      if (colors.has(lc)) fail(`R14.6 UNIQUE_RING_COLOR: step ${step.id} reuses ring color ${h.color}`);
      colors.add(lc);
      if (isPureWhite(h.color)) fail(`R14.6 UNIQUE_RING_COLOR: step ${step.id} ring color ${h.color} is pure white (use off-white)`);
      if (isRedHue(h.color)) fail(`R14.6 UNIQUE_RING_COLOR: step ${step.id} ring color ${h.color} is red / red-dominant (banned)`);
      // 9. SELECTORS (highlight element)
      if (!DATA_TOUR_ID_SELECTOR.test(h.element)) {
        fail(`R14.9 SELECTORS: step ${step.id} highlight element "${h.element}" is not a [data-tour-id="..."] selector`);
      }
    }

    // 7. BUTTON CAP
    const manualCount = step.advance.filter((a) => a.on === 'manual').length;
    if (manualCount > MAX_MANUAL_BUTTONS) {
      fail(`R14.7 BUTTON_CAP: step ${step.id} has ${manualCount} manual buttons (max ${MAX_MANUAL_BUTTONS})`);
    }
  }

  // --- Graph-wide checks ---
  const byId = new Map(pkg.steps.map((s) => [s.id, s]));

  // 2. REACHABLE: BFS from firstStepId over next edges (excluding _exit).
  const reachable = new Set();
  const queue = [pkg.firstStepId];
  while (queue.length) {
    const id = queue.shift();
    if (reachable.has(id)) continue;
    reachable.add(id);
    for (const a of byId.get(id).advance) {
      if (a.next !== EXIT && !reachable.has(a.next)) queue.push(a.next);
    }
  }
  for (const step of pkg.steps) {
    if (!reachable.has(step.id)) {
      fail(`R14.2 REACHABLE: step ${step.id} is not reachable from firstStepId "${pkg.firstStepId}"`);
    }
  }

  // 8. GRACEFUL END (covers 10 NO_FORCED_INFINITE_LOOP): _exit reachable from
  // every step. Reverse-reachability fixed point from the set of steps with a
  // direct `_exit` edge.
  const canExit = new Set();
  let changed = true;
  while (changed) {
    changed = false;
    for (const step of pkg.steps) {
      if (canExit.has(step.id)) continue;
      const reaches = step.advance.some((a) => a.next === EXIT || canExit.has(a.next));
      if (reaches) {
        canExit.add(step.id);
        changed = true;
      }
    }
  }
  for (const step of pkg.steps) {
    if (!canExit.has(step.id)) {
      fail(`R14.8 GRACEFUL_END: step ${step.id} has no path to ${EXIT} (trap / inescapable loop)`);
    }
  }

  return {
    moduleId: pkg.moduleId,
    version: pkg.version,
    firstStepId: pkg.firstStepId,
    steps: pkg.steps,
  };
};

export { GuideValidationError };
